import clsx from "clsx";
import { FC, useRef } from "react";
import { useTranslation } from "react-i18next";

import { ArrowRight } from "@/assets/icons/ds";
import { ButtonAnchor, ButtonLink } from "@/components/Interface/Button/Button";
import { useIdentificationStatus } from "@/hooks/identificationStatusQueryOptions";
import { useFWBMemberStatus } from "@/hooks/useFWBMemberStatus.ts";
import { useHasBillDunning } from "@/hooks/useHasBillDunning.ts";
import { useHasLockRequested } from "@/hooks/useHasLockRequested.ts";
import { useHasPendingMobileService } from "@/hooks/useHasPendingMobileService.ts";
import { useNeedsIDCheck } from "@/hooks/useNeedsIDCheck.ts";
import { portalRoutes } from "@/router/routes/portalRoutes.ts";
import { FCWithClassName } from "@/types";
import { getKycTodoConfig } from "@/utils/kycTodoConfig.ts";
import { NamespaceKey } from "@/utils/translationKey";

interface TodoRowProps {
  label: string;
  detail?: string;
  cta?: React.ReactNode;
}

/** One task row: orange bullet, label with optional detail, and a CTA control. */
const TodoRow: FC<TodoRowProps> = ({ label, detail, cta }) => (
  <li className="flex items-center gap-3 rounded-lg bg-surface-tertiary py-2 pl-3 pr-2">
    <span
      className="size-2 shrink-0 rounded-full bg-surface-accent-orange"
      aria-hidden="true"
    />
    <div className="min-w-0 flex-1">
      <span className="text-body-sm font-semibold leading-md text-foreground-primary">
        {label}
      </span>
      {detail && (
        <p className="mt-1 text-body-xs font-normal text-foreground-secondary">
          {detail}
        </p>
      )}
    </div>
    {cta}
  </li>
);

/** Icon-only arrow button that navigates to an external URL. */
const ExternalArrowButton = ({
  href,
  label,
}: {
  href: string;
  label: string;
}) => (
  <ButtonAnchor
    ariaLabel={label}
    href={href}
    icon={<ArrowRight />}
    size="sm"
    variant="secondary"
  />
);

type LinkTodo = { show: boolean; labelKey: NamespaceKey<"portal">; to: string };

/** Icon-only arrow button that navigates inside the portal. */
const InternalArrowButton = ({ to, label }: { to: string; label: string }) => (
  <ButtonLink
    ariaLabel={label}
    to={to}
    icon={<ArrowRight />}
    size="sm"
    variant="secondary"
  />
);

/**
 * Renders the "To-do" checklist: KYC identification, payment method (when a
 * SIM is lock-requested), open bills, and FWB mobile subscription prompts.
 * Returns null when there are no actionable items. Accepts `className` so
 * callers can hide it at specific breakpoints (e.g. `desktop:hidden` in the
 * content column when the Sidebar shows it).
 */
export const CockpitTodosSection: FCWithClassName = ({ className }) => {
  const { t } = useTranslation("portal");

  const needsIdCheck = useNeedsIDCheck();
  const staleTime = useRef(0);
  const { data: identificationStatus } = useIdentificationStatus(staleTime);

  const { isAtRiskOfRemoval } = useFWBMemberStatus();
  const hasBillDunning = useHasBillDunning();
  const hasPendingMobileService = useHasPendingMobileService();
  const hasLockRequested = useHasLockRequested();
  // `undefined` means "not known yet" - only show once we have a definitive answer.
  const showAddPaymentMethodTodo = hasLockRequested === true;
  // `undefined` means "not known yet" - only show the todo once we can
  // confirm there's no pending mobile service, not while still loading.
  const showFwbMobileAboTodo =
    isAtRiskOfRemoval && hasPendingMobileService === false;

  const kycTodo =
    needsIdCheck === true && identificationStatus
      ? getKycTodoConfig(identificationStatus.kycStatus)
      : null;
  // Portal-internal to-dos after the KYC one, in display order.
  const linkTodos = (
    [
      {
        show: showAddPaymentMethodTodo,
        labelKey: "cockpit.dashboard.todos.addPaymentMethod",
        to: portalRoutes.documents.payments,
      },
      {
        show: hasBillDunning,
        labelKey: "cockpit.dashboard.todos.payOpenBills",
        to: portalRoutes.documents.bills,
      },
      {
        show: showFwbMobileAboTodo,
        labelKey: "cockpit.dashboard.todos.fwbMobileAbo",
        to: portalRoutes.friendsWithBenefits.groupOverview,
      },
    ] satisfies LinkTodo[]
  ).filter(({ show }) => show);
  const todoCount = (kycTodo ? 1 : 0) + linkTodos.length;

  if (todoCount === 0) {
    return null;
  }

  return (
    <section
      data-testid="cockpit-todo-list"
      className={clsx(
        "flex flex-col gap-3 rounded-lg bg-surface-accent-ivory p-4",
        className,
      )}
    >
      <div className="flex items-center gap-2">
        <span
          className="flex size-6 shrink-0 items-center justify-center rounded-full bg-surface-accent-orange text-body-xs font-semibold leading-none text-foreground-primary-inverse"
          aria-hidden="true"
        >
          {todoCount}
        </span>
        <h2 className="text-headline-sm font-semibold leading-md text-foreground-primary">
          {t("cockpit.dashboard.todos.title")}
        </h2>
      </div>

      <ul className="flex flex-col gap-2">
        {kycTodo && (
          <TodoRow
            label={t(kycTodo.labelKey)}
            detail={kycTodo.detailKey ? t(kycTodo.detailKey) : undefined}
            cta={
              kycTodo.variant === "external" ? (
                <ExternalArrowButton
                  href={t(kycTodo.hrefKey!)}
                  label={t(kycTodo.labelKey)}
                />
              ) : kycTodo.variant === "internal" ? (
                <InternalArrowButton
                  to={kycTodo.href ?? portalRoutes.cockpit.idCheck}
                  label={t(kycTodo.labelKey)}
                />
              ) : undefined
            }
          />
        )}
        {linkTodos.map(({ labelKey, to }) => (
          <TodoRow
            key={labelKey}
            label={t(labelKey)}
            cta={<InternalArrowButton to={to} label={t(labelKey)} />}
          />
        ))}
      </ul>
    </section>
  );
};
